import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { DateRange, MatCalendar } from '@angular/material/datepicker';
import { MessageTypeEnum } from '@app/ngrx-message/constants/messageTypeEnum';
import { ProfileService } from '@app/profile/services/profile.service';
import { UserActions } from '@app/user/store/actions/action-types';
import { IonInput, ModalController, PopoverController } from '@ionic/angular';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { PlatformService } from '@panamax/app-state';
import { SimpleActionModalComponent } from '@shared/components/simple-action-modal/simple-action-modal.component';
import { FileService } from '@shared/services/file/file.service';
import { ModalService } from '@shared/services/modal/modal.service';
import { ToastService } from '@shared/services/toast/toast.service';
import moment, { Moment } from 'moment';
import { Subscription } from 'rxjs';
import { read, utils } from 'xlsx';
import { AcceptedFileTypesPopoverComponent } from '../accepted-file-types-popover/accepted-file-types-popover.component';

class CustomDateAdapter extends MomentDateAdapter {
  getDayOfWeekNames(style: 'long' | 'short' | 'narrow') {
    return ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  }
}

export const MONTH_NAME_YEAR = {
  display: {
    monthYearLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-create-custom-message-modal',
  templateUrl: './create-custom-message-modal.component.html',
  styleUrls: ['./create-custom-message-modal.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: CustomDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    { provide: MAT_DATE_FORMATS, useValue: MONTH_NAME_YEAR },
  ],
})
export class CreateCustomMessageModalComponent implements OnInit, OnDestroy {
  @Input() message: any;
  @Input() userId: number;
  @ViewChild('messageInput') messageInput: IonInput;
  @ViewChild('calendar') calendar: MatCalendar<Moment>;
  isTouch: boolean;
  selectedRangeValue: DateRange<Moment> | undefined;
  customMessageFormGroup: UntypedFormGroup;
  today = new Date();
  acceptedFileTypes = ['.csv', '.xls', '.xlsx'];
  mobileDateRangeValue: string;
  mobileDateRangeMode = false;
  fileInProgress: boolean;
  fileAttached = false;
  fileName: string;
  fileMissingHeader = false;
  fileInvalidType = false;
  fileInvalidData = false;
  fileErrorInRows = [];
  onSaveSuccessCustomMessageSub$: Subscription;
  onSaveFailCustomMessageSub$: Subscription;
  constructor(
    protected modalController: ModalController,
    protected platformService: PlatformService,
    protected popoverController: PopoverController,
    protected fb: UntypedFormBuilder,
    protected profileService: ProfileService,
    protected fileService: FileService,
    protected translateService: TranslateService,
    private modalService: ModalService,
    protected toastService: ToastService,
    protected actionSubject: ActionsSubject,
  ) {}

  ngOnInit() {
    this.isTouch = this.platformService.isTouch.value;
    const urlRegex = /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+$/;
    this.customMessageFormGroup = this.fb.group({
      message: [
        '',
        Validators.compose([Validators.required, Validators.maxLength(90)]),
      ],
      customerList: ['', Validators.required],
      cta: ['', Validators.pattern(urlRegex)],
      dateFrom: [''],
      dateTo: [''],
      messageId: [''],
      userId: [''],
    });
    if (this.message) {
      const start =
        this.message?.startDate?.toString()?.indexOf('1970') < 0
          ? moment.utc(this.message.startDate)
          : null;
      const end =
        this.message?.expireDate?.toString()?.indexOf('1970') < 0
          ? moment.utc(this.message.expireDate)
          : null;
      this.customMessageFormGroup.patchValue({
        message: this.message?.text,
        cta: this.message?.link,
        dateFrom: start,
        dateTo: end,
        customerList: this.message?.customers,
        messageId: this.message?.messageId,
        userId: this.message?.userId ? this.message?.userId : this.userId,
      });
      this.fileName = 'CustomerList.csv';
      this.fileAttached = true;
      this.selectedRangeValue = new DateRange<Moment>(start, end);
      if (
        this.platformService?.platformType ===
        this.platformService?.platformEnum.mobile
      ) {
        this.mobileDateRangeValue =
          this.selectedRangeValue?.start?.format('M/D/YYYY') +
          ' - ' +
          this.selectedRangeValue?.end?.format('M/D/YYYY');
      }
    }
    //Check if custom message is saved successfully
    this.onSaveSuccessCustomMessageSub$ = this.actionSubject
      .pipe(ofType(UserActions.saveCustomMessageSuccess))
      .subscribe(data => {
        this.toastService.dismissToast();
        this.toastService.presentToastMsg(
          this.message
            ? this.translateService.instant(
                'i18n.profilePage.customMessages.customMessageEditedToast',
              )
            : this.translateService.instant(
                'i18n.profilePage.customMessages.customMessageCreatedToast',
              ),
          'green-toast',
          MessageTypeEnum.success,
          [],
        );
        this.profileService.getCustomMessages();
        this.dismissModal();
      });
    //Check if custom message failed to save
    this.onSaveFailCustomMessageSub$ = this.actionSubject
      .pipe(ofType(UserActions.saveCustomMessageFail))
      .subscribe(data => {
        this.toastService.dismissToast();
        this.toastService.presentToastMsg(
          this.translateService.instant(
            'i18n.profilePage.customMessages.customMessageCreatedFailToast',
          ),
          'red-toast',
          MessageTypeEnum.error,
          [{ icon: 'close-circle' }],
          0,
        );
      });
  }

  ngOnDestroy(): void {
    if (this.onSaveSuccessCustomMessageSub$) {
      this.onSaveSuccessCustomMessageSub$.unsubscribe();
    }
    if (this.onSaveFailCustomMessageSub$) {
      this.onSaveFailCustomMessageSub$.unsubscribe();
    }
  }

  dismissModal() {
    this.modalController.dismiss();
  }

  resetForm() {
    this.selectedRangeValue = new DateRange<Moment>(null, null);
    this.mobileDateRangeValue = '';
    this.customMessageFormGroup.patchValue({
      dateFrom: '',
      dateTo: '',
    });
    this.calendar._goToDateInView(moment(new Date()), 'month');
  }

  selectedDateRangeChange(date: Moment) {
    if (date) {
      this.customMessageFormGroup?.markAsDirty();
      if (!this.selectedRangeValue?.start || this.selectedRangeValue.end) {
        this.selectedRangeValue = new DateRange<Moment>(date, null);
      } else {
        const start = this.selectedRangeValue.start;
        const end = date;
        if (end < start) {
          this.selectedRangeValue = new DateRange<Moment>(end, start);
        } else {
          this.selectedRangeValue = new DateRange<Moment>(start, end);
        }
      }
      if (this.selectedRangeValue?.start && this.selectedRangeValue.end) {
        this.customMessageFormGroup.patchValue({
          dateFrom: this.selectedRangeValue.start.format('M/D/YYYY'),
          dateTo: this.selectedRangeValue.end.format('M/D/YYYY'),
        });
        this.mobileDateRangeValue =
          this.selectedRangeValue?.start?.format('M/D/YYYY') +
          ' - ' +
          this.selectedRangeValue?.end?.format('M/D/YYYY');
      } else if (this.selectedRangeValue?.start) {
        this.customMessageFormGroup.patchValue({
          dateFrom: this.selectedRangeValue.start.format('M/D/YYYY'),
          dateTo: '',
        });
        this.mobileDateRangeValue =
          this.selectedRangeValue?.start?.format('M/D/YYYY') +
          ' - ' +
          this.translateService.instant('i18n.common.notApplicable');
      } else {
        this.customMessageFormGroup.patchValue({
          dateFrom: '',
          dateTo: '',
        });
        this.mobileDateRangeValue = '';
      }
    }
  }

  async saveMessage() {
    const formValues = this.customMessageFormGroup.value;
    const createdMessage = {
      messageId: formValues?.messageId,
      userId: formValues?.userId ? formValues?.userId : this.userId,
      text: formValues?.message,
      addDtm: new Date(),
      startDate: formValues?.dateFrom,
      expireDate: formValues?.dateTo,
      link: formValues?.cta,
      customers: formValues?.customerList,
    };
    if (!formValues?.dateFrom || !formValues?.dateTo) {
      this.modalService.setModalOptions(
        this.platformService.isTouch.value,
        SimpleActionModalComponent,
        {
          title: this.translateService.instant(
            'i18n.profilePage.customMessages.createMessageNoDateModalTitle',
          ),
          content: this.translateService.instant(
            'i18n.profilePage.customMessages.createMessageNoDateModalContent',
          ),
          leftButtonText: this.translateService.instant(
            'i18n.profilePage.customMessages.createMessageNoDateModalCancel',
          ),
          rightButtonText: this.translateService.instant(
            'i18n.profilePage.customMessages.createMessageNoDateModalConfirm',
          ),
          note: null,
          exitButtonForTablet: true,
        },
        'simple-action-modal-nested',
      );

      const modal = await this.modalController.create(
        this.modalService.modalOptions,
      );
      const parentModal = await this.modalController.getTop();
      modal?.onDidDismiss().then((response: any) => {
        if (response?.data?.dispatchAction) {
          this.profileService.saveCustomMessage(createdMessage);
        } else {
          if (parentModal) parentModal.hidden = false;
        }
      });
      if (parentModal) parentModal.hidden = true;
      return await modal?.present();
    } else {
      this.profileService.saveCustomMessage(createdMessage);
    }
  }

  async showAcceptedFileTypesPopover(event: any) {
    const acceptedFilePopover = await this.popoverController.create({
      component: AcceptedFileTypesPopoverComponent,
      event,
      componentProps: {
        fileTypesList: this.acceptedFileTypes,
      },
      cssClass: 'accepted-file-types-popover',
      mode: 'ios',
      showBackdrop: false,
      backdropDismiss: true,
      triggerAction: 'hover',
    });
    await acceptedFilePopover?.present();
  }

  shouldCloseAcceptedFileTypesPopover(event: any) {
    setTimeout(async () => {
      if (this.popoverController.getTop()) {
        await this.popoverController.dismiss();
      }
    }, 100);
  }

  showDateRengeMobile() {
    this.mobileDateRangeMode = true;
  }

  async validateMaxChars(event: any) {
    if (this.messageInput?.value?.toString()?.length > 90) {
      const messageInputEl = await this.messageInput?.getInputElement();
      messageInputEl?.blur();
      messageInputEl?.focus();
      return false;
    }
  }

  downloadTemplate() {
    this.profileService.downloadTemplate();
  }

  async uploadFile() {
    this.customMessageFormGroup?.markAsDirty();
    const file = await this.fileService.addFile();
    let fileData = atob(await this.fileService.loadFileData(file?.filepath));
    const extReg = /(?:\.([^.]+))?$/;
    const fileExt = extReg.exec(file?.name.toString().toLowerCase())[0];
    let customers;
    let customersObj = [];
    if (this.acceptedFileTypes.indexOf(fileExt) > -1) {
      this.resetFileErrors();
      this.fileName = file?.name;
      if (fileExt === '.xls' || fileExt === '.xlsx') {
        const workBook = read(fileData, { type: 'binary' });
        fileData = utils.sheet_to_csv(workBook.Sheets[workBook.SheetNames[0]], {
          RS: '\r\n',
        });
      }
      let splittedData = fileData
        ?.split(/\r\n/)
        .filter(a => !!a && a.trim() !== ',');
      if (
        splittedData &&
        splittedData[0].trim() === 'Customer Number,Division Number'
      ) {
        customers = splittedData.slice(1);
        customers.forEach((customer, index) => {
          const customerObj = {
            customerNumber: +customer.split(',')[0].trim(),
            divisionNumber: +customer.split(',')[1].trim(),
          };
          if (
            !customersObj.find(
              c =>
                c.customerNumber === customerObj.customerNumber &&
                c.divisionNumber === customerObj.divisionNumber,
            )
          ) {
            customersObj.push({
              customerNumber: +customer.split(',')[0].trim(),
              divisionNumber: +customer.split(',')[1].trim(),
            });
            this.validateUploadedCustomer(customerObj, index);
          }
        });
        const isValid =
          customersObj.length > 0 &&
          !this.fileInvalidData &&
          this.fileErrorInRows?.length === 0;
        if (this.fileInvalidData) this.fileErrorInRows = [];
        if (customersObj.length === 0) {
          this.resetFileErrors();
          this.fileInvalidData = true;
        }
        if (isValid) {
          this.customMessageFormGroup.patchValue({
            customerList: customersObj,
          });
          this.fileAttached = true;
          this.resetFileErrors();
        } else {
          this.fileAttached = false;
        }
      } else {
        this.resetFileErrors();
        this.fileMissingHeader = true;
      }
    } else {
      this.resetFileErrors();
      this.fileInvalidType = true;
    }
  }

  validateUploadedCustomer(customer: any, index: number): boolean {
    let rv = true;
    if (
      !customer.customerNumber ||
      !customer.divisionNumber ||
      isNaN(customer.customerNumber) ||
      isNaN(customer.divisionNumber)
    ) {
      if (this.fileErrorInRows.length >= 5) {
        this.fileInvalidData = true;
      } else {
        this.fileErrorInRows?.push(index + 1);
      }
      rv = false;
    }
    return rv;
  }

  deleteFile() {
    this.customMessageFormGroup.patchValue({ customerList: null });
    this.fileAttached = false;
  }

  resetFileErrors() {
    this.fileErrorInRows = [];
    this.fileInvalidData = false;
    this.fileInvalidType = false;
    this.fileMissingHeader = false;
  }
}
